import { EConditionType, EFontStyle, EHorizontalPosition, EOverlayType, EVerticalPosition } from '@common/enums';
import { DEFAULT_SHADOW_SETTINGS } from '@common/types/shadow-settings.type';

import type { CssVars } from '../../../css-vars';
import { legacyBorderSideVar } from '../../../features/border/border.css-vars';
import { shadowValueVar } from '../../../features/shadow/shadow.css-vars';
import { flexAlignVar, textAlignVar } from '../../../mixins/alignment/alignment.css-vars';
import { pxVar } from '../../../mixins/number-with-unit/number-with-unit.css-vars';
import { rotateVar } from '../../../mixins/rotation/rotation.css-vars';
import type { ImageEntity } from '../schemas';

/**
 * CSS custom properties for the image widget — the LESS twin of the former JSS
 * `getStyles`. ONE module shared by both apps; the static rules consuming the
 * variables live in `@sites/entity-core/.../image.base.less` (imported by each
 * component's `.less`).
 *
 * A `null` value removes the property from the element so the consuming
 * declaration falls back to its `var()` default — mirroring the omitted-key
 * behaviour of the old JSS object. Modifier classes (returned by the
 * `*CssClasses` helpers) gate the variable-driven rules so an unset property
 * never clobbers a base style.
 */
export type ImageCssVars = CssVars;

type ImageWidget = Pick<ImageEntity, 'type' | 'data' | 'styles'>;

function shadowValue(data: ImageEntity['data']): string {
    const s = data.shadowSettings ?? DEFAULT_SHADOW_SETTINGS;
    return shadowValueVar(s);
}

/** Outer wrapper (`.img-widget-width`) — alignment, rotation, z-index per device. */
export function getImageRootCssVars(widget: ImageWidget): CssVars {
    const { data } = widget;
    const isZero = widget.type === 'zero';
    const position = data.position as Record<string, number | null | undefined> | null | undefined;

    const mobileJustify = flexAlignVar(data.mobileHorizontalAlign ?? data.horizontalAlign);
    const tabletJustify = data.tabletHorizontalAlign ? flexAlignVar(data.tabletHorizontalAlign) : mobileJustify;

    return {
        '--image-z-index': position?.zIndex != null ? `${position.zIndex}` : null,
        '--image-justify': flexAlignVar(data.horizontalAlign),
        '--image-justify-mobile': mobileJustify,
        '--image-justify-tablet': tabletJustify,
        // Zero widgets are rotated by the zero-block host, so the widget itself
        // emits `none` (rotate(0deg) would create a stacking context).
        '--image-transform': isZero ? 'none' : rotateVar(position?.rotate),
        '--image-transform-mobile': isZero ? 'none' : rotateVar(position?.mobileRotate),
        '--image-transform-tablet': isZero ? 'none' : rotateVar(position?.tabletRotate),
    };
}

export function getImageRootCssClasses(widget: ImageWidget): string {
    return widget.type === 'zero' ? 'image-zero' : '';
}

/** Image box (`.image-wrapper`) — radius, sizing, shadow, border. */
export function getImageBoxCssVars(widget: ImageWidget): CssVars {
    const { data, styles } = widget;
    const isZero = widget.type === 'zero';

    const corner = (value: number | null | undefined): string =>
        value || value === 0 ? pxVar(value) : pxVar(styles.borderRadius ?? 0);

    const vars: CssVars = {
        '--image-radius-tl': corner(styles.borderTopLeftRadius),
        '--image-radius-tr': corner(styles.borderTopRightRadius),
        '--image-radius-br': corner(styles.borderBottomRightRadius),
        '--image-radius-bl': corner(styles.borderBottomLeftRadius),
        '--image-max-width': null,
        '--image-padding-top': null,
        '--image-shadow': null,
        '--image-border': null,
        '--image-border-top': null,
        '--image-border-right': null,
        '--image-border-bottom': null,
        '--image-border-left': null,
    };

    if (!data.resizeType || data.resizeType === 'scale') {
        vars['--image-max-width'] = data.image ? pxVar(styles.maxWidth) : '100%';
    } else if (data.resizeType === 'fill') {
        if (!data.image) {
            vars['--image-padding-top'] = '200px';
        } else if (data.image.resizedHeight && data.image.resizedWidth) {
            vars['--image-padding-top'] = (data.image.resizedHeight / data.image.resizedWidth) * 100 + '%';
        } else if (!isZero) {
            vars['--image-padding-top'] = '100%';
        }
    }

    if (data.isShadow) {
        vars['--image-shadow'] = shadowValue(data);
    }

    if (data.isBorder) {
        const abs = styles.advancedBorderSettings;
        if (abs) {
            if (abs.isIndependentBorders) {
                vars['--image-border-top'] = legacyBorderSideVar(abs, 'top');
                vars['--image-border-right'] = legacyBorderSideVar(abs, 'right');
                vars['--image-border-bottom'] = legacyBorderSideVar(abs, 'bottom');
                vars['--image-border-left'] = legacyBorderSideVar(abs, 'left');
            } else {
                vars['--image-border'] = legacyBorderSideVar(abs, 'top');
            }
        } else {
            vars['--image-border'] = legacyBorderSideVar(styles);
        }
    }

    return vars;
}

export function getImageBoxCssClasses(widget: ImageWidget, isSpecific?: boolean): string {
    const { data, styles } = widget;
    const classes: string[] = [];

    classes.push(data.resizeType === 'fill' ? 'image-fill' : 'image-scale');

    if (data.isShadow) {
        classes.push('image-shadow');
    }

    if (data.isBorder) {
        classes.push(styles.advancedBorderSettings?.isIndependentBorders ? 'image-border-advanced' : 'image-border');
    } else if (data.isBorder === false) {
        classes.push('image-no-border');
    }

    if (!data.image) {
        classes.push('is-empty');
        if (widget.type === 'zero') {
            classes.push('image-zero');
        }
        if (isSpecific) {
            classes.push('image-specific');
        }
    }

    return classes.join(' ');
}

/** The `<img>` element — scale/fill layout + hover effect. */
export function getImageElementCssClasses(widget: ImageWidget): string {
    const { data } = widget;
    const classes: string[] = ['sp-image', 'shape'];

    if (!data.image) {
        classes.push('is-empty');
        return classes.join(' ');
    }

    if (data.resizeType === 'fill') {
        classes.push('image-fill');
    }

    if (data.image.isImageEffect) {
        if (data.image.overlay === EOverlayType.INCREASE) {
            classes.push('image-hover-increase');
        } else if (data.image.overlay === EOverlayType.DECREASE) {
            classes.push('image-hover-decrease');
        }
    }

    return classes.join(' ');
}

/**
 * `-webkit-mask-image` for the masked image (`null` when no mask shape).
 *
 * The shape SVGs are shipped with the editor bundle, so the relative path only
 * resolves in the editor (its `<base href>` is `/websites/builder/`). The SSR
 * builder renders published pages under arbitrary URLs and doesn't bundle the
 * shapes at all — it passes `resolveAssetUrl` (`UtilsService.formatImgSrc`) to
 * turn the path into an absolute one. A mask image that fails to load hides the
 * `<img>` completely, so an unresolved path means an invisible image.
 */
export function getImageMaskValue(
    image: ImageEntity['data']['image'],
    resolveAssetUrl: (path: string) => string = (path) => path
): string | null {
    return image?.maskShape
        ? `url('${resolveAssetUrl(`./assets/img/websites/mask-shapes/${image.maskShape}.svg`)}')`
        : null;
}

/** Overlay caption box (`.sp-image-description`). */
export function getImageDescriptionCssVars(widget: ImageWidget): CssVars {
    const image = widget.data.image;
    return {
        '--image-desc-padding': image?.description
            ? `${image.paddingTop}px ${image.paddingRight}px ${image.paddingBottom}px ${image.paddingLeft}px`
            : '0',
        '--image-desc-bg': image?.textBackground ?? null,
    };
}

export function getImageDescriptionCssClasses(widget: ImageWidget): string {
    const image = widget.data.image;
    const classes: string[] = [];

    if (image?.condition === EConditionType.ON_HOVER && image?.isImageEffect) {
        classes.push('image-desc-hover');
    }

    // No description text → bottom-left (the JSS default); otherwise position
    // by `textPosition`.
    if (!image?.description) {
        classes.push('image-desc-bottom');
    } else if (image.textPosition === EVerticalPosition.T) {
        classes.push('image-desc-top');
    } else if (image.textPosition === EVerticalPosition.M) {
        classes.push('image-desc-middle');
    } else {
        classes.push('image-desc-bottom');
    }

    return classes.join(' ');
}

/** Overlay caption text (`.sp-image-text`). Only meaningful when a description exists. */
/**
 * The slice of the theme's paragraph styles this builder reads — the host passes
 * `themePStyles` from `@sites/site-theme`, whose values are `var(--theme-font-*)`
 * strings threaded through unchanged. Injected rather than imported because
 * entity-core stays framework-free.
 */
export type ImageTextThemeStyles = {
    fontSize?: unknown;
    fontFamily?: unknown;
    lineHeight?: unknown;
};

/**
 * Caption text variables. `sizeCoefficient` is the host's
 * `commonFontSizeCoefficient` (the S/M/L multiplier).
 */
export function getImageTextCssVars(
    widget: ImageWidget,
    textStyles: ImageTextThemeStyles,
    sizeCoefficient: Record<string, number>
): ImageCssVars {
    const image = widget.data.image;
    if (!image?.description) {
        return {};
    }

    const coefficient = sizeCoefficient[image.textSize as string] ?? 1;
    const fontStyle = image.textStyle ?? [];

    return {
        '--image-text-justify': flexAlignVar(image.textJustify!),
        '--image-text-align': textAlignVar(image.textJustify!),
        '--image-text-margin-left': image.link ? '16px' : '0',
        '--image-text-margin-right': image.link ? `calc(${textStyles.fontSize} * ${coefficient} + 15px)` : '0',
        '--image-text-font-family': (textStyles.fontFamily as string) ?? null,
        '--image-text-line-height': (textStyles.lineHeight as string) ?? null,
        '--image-text-font-size': pxVar(image.fontSize),
        '--image-text-color': image.textColor ?? null,
        '--image-text-font-weight': fontStyle.includes(EFontStyle.B) ? 'bold' : 'initial',
        '--image-text-font-style': fontStyle.includes(EFontStyle.I) ? 'italic' : 'initial',
        '--image-text-transform': fontStyle.includes(EFontStyle.AT) ? 'uppercase' : 'initial',
    };
}
